import CampaignSegmentationSummary from '@/components/audience/CampaignSegmentationSummary';
import RichMessagePreview from '@/components/ads/RichMessagePreview';
import AdCampaignTypeBadges from '@/components/ui/AdCampaignTypeBadges';
import { useTranslation } from '@/hooks/useTranslation';
import { useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, Zap } from 'lucide-react';
import Button from '@/components/ui/Button';
import AdCampaignStatusBadge from '@/components/ui/AdCampaignStatusBadge';
import { BannerPreviewModal, TextPreviewModal, VideoPreviewModal } from '@/components/ads';
import { useAdCampaign } from '@/hooks/useAdCampaigns';
import { formatDateTime } from '@/utils/formatters';
import { AdCampaign, AdCampaignType, AdScheduleDayOfWeek, BannerOverlayPosition } from '@/types/ads';
import DisplayNowModal from '@/components/ads/DisplayNowModal';
import { resolveTextAdFontLabel, resolveTextAdFontFamily, TEXT_AD_DEFAULT_FONT } from '@/constants/textAdFonts';

const resolveCampaignType = (campaign: AdCampaign): AdCampaignType | undefined => {
  if (campaign.type) return campaign.type;
  if (campaign.videoUrl || campaign.adTagUrl) return 'VIDEO';
  if (campaign.bannerImageUrl) return 'BANNER';
  if (campaign.text) return 'TEXT';
  return undefined;
};

const scheduleDayLabels: Record<AdScheduleDayOfWeek, string> = {
  MONDAY: 'Lun',
  TUESDAY: 'Mar',
  WEDNESDAY: 'Mié',
  THURSDAY: 'Jue',
  FRIDAY: 'Vie',
  SATURDAY: 'Sáb',
  SUNDAY: 'Dom',
};

const orderedScheduleDays: AdScheduleDayOfWeek[] = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

const normalizeTimeLabel = (value?: string) => {
  if (!value) return '—';
  const raw = value.slice(0, 8);
  const [hoursText, minutesText, secondsText = '00'] = raw.split(':');
  const hours = Number(hoursText);
  const minutes = Number(minutesText);
  const seconds = Number(secondsText);
  if (Number.isNaN(hours) || Number.isNaN(minutes) || Number.isNaN(seconds)) return raw;

  const period = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  const secondsLabel = seconds > 0 ? `:${String(seconds).padStart(2, '0')}` : '';
  return `${hour12}:${String(minutes).padStart(2, '0')}${secondsLabel} ${period}`;
};

const formatScheduleDays = (days: AdScheduleDayOfWeek[]) => {
  const sorted = orderedScheduleDays.filter((day) => days.includes(day));
  if (sorted.length === 0) return '—';

  const ranges: string[] = [];
  let rangeStart = 0;

  for (let i = 1; i <= sorted.length; i += 1) {
    const currentIndex = i < sorted.length ? orderedScheduleDays.indexOf(sorted[i]) : -1;
    const previousIndex = orderedScheduleDays.indexOf(sorted[i - 1]);
    const isConsecutive = i < sorted.length && currentIndex === previousIndex + 1;

    if (isConsecutive) continue;

    const startDay = sorted[rangeStart];
    const endDay = sorted[i - 1];
    ranges.push(
      startDay === endDay
        ? scheduleDayLabels[startDay]
        : `${scheduleDayLabels[startDay]}-${scheduleDayLabels[endDay]}`
    );
    rangeStart = i;
  }

  return ranges.join(', ');
};

const ViewCampaign = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const campaignQuery = useAdCampaign(id || '');

  const [creativeIndex, setCreativeIndex] = useState(0);
  const [isTextPreviewOpen, setIsTextPreviewOpen] = useState(false);
  const [isBannerPreviewOpen, setIsBannerPreviewOpen] = useState(false);
  const [isVideoPreviewOpen, setIsVideoPreviewOpen] = useState(false);
  const [isDisplayNowOpen, setIsDisplayNowOpen] = useState(false);
  const previewButtonRef = useRef<HTMLButtonElement | null>(null);
  const scheduleLabels = useMemo(() => {
    const schedules = campaignQuery.data?.schedules ?? [];
    if (schedules.length === 0) return [];
    return schedules.map((schedule) => {
      const days = formatScheduleDays(schedule.daysOfWeek ?? []);
      return `${days || '—'} • ${normalizeTimeLabel(schedule.triggerTime)}`;
    });
  }, [campaignQuery.data?.schedules]);

  if (campaignQuery.isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow p-8 flex items-center justify-center text-gray-600">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando...
          </div>
        </div>
      </div>
    );
  }

  if (campaignQuery.isError || !campaignQuery.data) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-red-600 font-medium">No se pudo cargar la campaña</div>
            <div className="text-gray-600 mt-2">{(campaignQuery.error as Error)?.message}</div>
            <div className="mt-4">
              <Button onClick={() => navigate('/ads/campaigns')} variant="outline">
                Volver
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const rootCampaign = campaignQuery.data;
  const selectedCreative = rootCampaign.creatives?.[creativeIndex];
  const campaign = selectedCreative ? { ...rootCampaign, ...selectedCreative.content } : rootCampaign;
  const enabledContent = rootCampaign.creatives?.filter(c => c.enabled).map(c => c.content) ?? [rootCampaign];
  const type = resolveCampaignType(campaign);
  const durationMode = campaign.durationMode ?? 'DATE_RANGE';
  const showOnLiveTv = enabledContent.some(c => c.showOnLiveTv);
  const showOnVod = enabledContent.some(c => c.showOnVod);
  const includedChannels = campaign.includedChannels ?? [];
  const excludedChannels = campaign.excludedChannels ?? [];

  const textFontSize = campaign.textFontSize ?? 24;
  const textFontColor = campaign.textFontColor ?? '#ffffff';
  const textFontFamily = campaign.textFontFamily ?? TEXT_AD_DEFAULT_FONT;
  const textBackgroundColor = campaign.textBackgroundColor ?? '#111827';
  const textTickerSpeedSeconds = campaign.textTickerSpeedSeconds ?? 12;
  const textPosition = (campaign.bannerPosition ?? 'BOTTOM') as BannerOverlayPosition;
  const bannerScaleFactor = campaign.bannerScaleFactor ?? 1;
  const newsTickerSourceType = campaign.newsTickerSourceType ?? 'MANUAL';
  const showManualNews = newsTickerSourceType === 'MANUAL' || newsTickerSourceType === 'MANUAL_AND_RSS';
  const manualNewsItems = showManualNews ? (campaign.newsTickerItems ?? []) : [];
  const newsTickerPreviewText = manualNewsItems.join(campaign.newsTickerSeparator || ' • ');

  const previewDisabled = (() => {
    if (type === 'VIDEO') return !campaign.videoUrl;
    if (type === 'BANNER') return !campaign.bannerImageUrl;
    if (type === 'TEXT') return !campaign.text;
    if (type === 'NEWS_TICKER') return !newsTickerPreviewText && !campaign.newsTickerRssUrl;
    return true;
  })();

  const previewHelper = (() => {
    if (type === 'VIDEO' && !campaign.videoUrl) return 'Sube un video para previsualizar.';
    if (type === 'BANNER' && !campaign.bannerImageUrl) return 'Sube una imagen para previsualizar.';
    if (type === 'TEXT' && !campaign.text) return 'Agrega el texto para previsualizar.';
    if (type === 'NEWS_TICKER' && !newsTickerPreviewText && !campaign.newsTickerRssUrl) return 'Agrega noticias o una URL RSS.';
    if (!type) return 'Tipo de campaña desconocido.';
    return '';
  })();

  const closeDisplayNowModal = () => {
    setIsDisplayNowOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-4">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button variant="link" onClick={() => navigate('/ads/campaigns')}>
          ← Volver a campañas
        </Button>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{campaign.name}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <AdCampaignStatusBadge status={campaign.status} />
                <AdCampaignTypeBadges campaign={rootCampaign} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                className="flex items-center gap-2"
                disabled={campaign.status !== 'ACTIVE' || !enabledContent.some(c => c.showOnLiveTv || c.showOnVod || c.showOnRadio)}
                onClick={() => {
                  setIsDisplayNowOpen(true);
                }}
              >
                <Zap className="h-4 w-4" />
                Mostrar ahora
              </Button>
              <Button
                type="button"
                variant="outline"
                ref={previewButtonRef}
                disabled={previewDisabled}
                onClick={() => {
                  if (type === 'VIDEO') setIsVideoPreviewOpen(true);
                  if (type === 'BANNER') setIsBannerPreviewOpen(true);
                  if (type === 'TEXT') setIsTextPreviewOpen(true);
                  if (type === 'NEWS_TICKER') setIsTextPreviewOpen(true);
                }}
              >
                Vista Previa
              </Button>
              <Button type="button" onClick={() => navigate(`/ads/campaigns/${campaign.id}/edit`)}>
                Editar
              </Button>
            </div>
          </div>

          {!!rootCampaign.creatives?.length && <section className="mt-5 rounded-lg border border-gray-200 p-4">
            <h2 className="text-sm font-semibold text-gray-800">{t.adCreatives.title}</h2>
            <p className="mt-1 text-xs text-gray-500">{t.adCreatives.choose}</p>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {rootCampaign.creatives.map((creative, index) => <button type="button" key={creative.id ?? index}
                aria-pressed={creativeIndex === index} onClick={() => setCreativeIndex(index)}
                className={`rounded-md border p-3 text-left text-sm ${creativeIndex === index ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'}`}>
                <div className="font-medium">{creative.name}{!creative.enabled ? ` · ${t.adCreatives.disabled}` : ''}</div>
                <div className="mt-1 text-xs text-gray-500">{[creative.content.type, creative.content.showOnLiveTv && t.adCreatives.live,
                  creative.content.showOnVod && t.adCreatives.vod, creative.content.showOnRadio && t.adCreatives.radio,
                  creative.content.home?.enabled && t.adCreatives.home].filter(Boolean).join(' · ')}</div>
              </button>)}
            </div>
          </section>}

          {previewHelper && (
            <div className="mt-2 text-xs text-gray-500">
              {previewHelper}
            </div>
          )}

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-gray-200 p-4">
              <h2 className="text-sm font-semibold text-gray-800 mb-3">Programación</h2>
              <div className="space-y-2 text-sm text-gray-700">
                <div className="flex items-center justify-between">
                  <span>Vigencia</span>
                  <span className="font-medium">{durationMode === 'UNTIL_DEACTIVATED' ? 'Hasta desactivar' : 'Rango de fechas'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Inicio</span>
                  <span className="font-medium">
                    {durationMode === 'UNTIL_DEACTIVATED' ? 'Inmediata / sin fecha' : formatDateTime(campaign.startAt, '—')}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Fin</span>
                  <span className="font-medium">
                    {durationMode === 'UNTIL_DEACTIVATED' ? 'Sin fecha de fin' : formatDateTime(campaign.endAt, '—')}
                  </span>
                </div>
                <div className="pt-2">
                  <div className="text-xs text-gray-500 mb-1">Horarios recurrentes</div>
                  {scheduleLabels.length === 0 ? (
                    <div className="text-sm text-gray-700">Sin restricción horaria</div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {scheduleLabels.map((label, index) => (
                        <span key={`${label}-${index}`} className="px-2 py-0.5 rounded-md text-xs border border-gray-200 bg-gray-50">
                          {label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 p-4">
              <h2 className="text-sm font-semibold text-gray-800 mb-3">Prioridad y distribución</h2>
              <div className="space-y-2 text-sm text-gray-700">
                <div className="flex items-center justify-between">
                  <span>Prioridad</span>
                  <span className="font-medium">{campaign.priority}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Peso</span>
                  <span className="font-medium">{campaign.weight ?? '—'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Máx impresiones por usuario por día</span>
                  <span className="font-medium">{campaign?.maxImpressionsPerUserPerDay ?? 'Sin límite'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Mostrar en TV en vivo</span>
                  <span className="font-medium">{showOnLiveTv ? 'Sí' : 'No'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Mostrar en VOD</span>
                  <span className="font-medium">{showOnVod ? 'Sí' : 'No'}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span>{t.homeCampaign.enabled}</span>
                  <span className="font-medium">{enabledContent.some(c => c.home?.enabled) ? t.common.yes : t.common.no}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span>{t.radioCampaign.enabled}</span>
                  <span className="font-medium">{enabledContent.some(c => c.showOnRadio) ? t.common.yes : t.common.no}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-gray-200 p-4">
              <h2 className="text-sm font-semibold text-gray-800 mb-3">Segmentación</h2>
              <div className="space-y-2 text-sm text-gray-700">
                <CampaignSegmentationSummary embedded locations={rootCampaign.locationTargets ?? []} audience={rootCampaign.audience ?? {}} />
                <div>
                  <div className="text-xs text-gray-500 mb-1">Canales incluidos</div>
                  {includedChannels.length === 0 ? (
                    <div className="text-sm text-gray-700">Todos</div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {includedChannels.map((channel) => (
                        <span key={channel.id} className="px-2 py-0.5 rounded-md text-xs border border-gray-200 bg-gray-50">
                          {channel.name || channel.id}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Canales excluidos</div>
                  {excludedChannels.length === 0 ? (
                    <div className="text-sm text-gray-700">Ninguno</div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {excludedChannels.map((channel) => (
                        <span key={channel.id} className="px-2 py-0.5 rounded-md text-xs border border-gray-200 bg-gray-50">
                          {channel.name || channel.id}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 p-4">
              <h2 className="text-sm font-semibold text-gray-800 mb-3">{selectedCreative?.name ?? 'Contenido'}</h2>
              {type === 'VIDEO' && (
                <div className="space-y-2 text-sm text-gray-700">
                  <div className="flex items-center justify-between">
                    <span>Se puede cerrar</span>
                    <span className="font-medium">{campaign.videoDismissible ? 'Sí' : 'No'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Mínimo para cerrar</span>
                    <span className="font-medium">
                    {campaign.videoMinSecondsBeforeDismiss ? `${campaign.videoMinSecondsBeforeDismiss}s` : '—'}
                  </span>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Video</div>
                    {campaign.videoUrl ? (
                      <div className="relative w-full aspect-video rounded-xl overflow-hidden border border-gray-200 bg-black shadow-inner">
                        <video
                          src={campaign.videoUrl}
                          className="h-full w-full object-cover"
                          controls
                          playsInline
                        />
                      </div>
                    ) : (
                      <div className="text-sm text-gray-700">—</div>
                    )}
                  </div>
                </div>
              )}

              {type === 'BANNER' && (
                <div className="space-y-3 text-sm text-gray-700">
                  <div className="flex items-center justify-between">
                    <span>Posición</span>
                    <span className="font-medium">
                    {campaign.bannerPosition === 'TOP' ? 'Arriba' : campaign.bannerPosition === 'BOTTOM' ? 'Abajo' : '—'}
                  </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Duración</span>
                    <span className="font-medium">
                    {campaign.showTimeInSeconds ? `${campaign.showTimeInSeconds}s` : '—'}
                  </span>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Imagen</div>
                    {campaign.bannerImageUrl ? (
                      <img
                        src={campaign.bannerImageUrl}
                        alt="Vista previa del banner"
                        className="max-h-48 w-full object-contain rounded border border-gray-200 bg-white"
                      />
                    ) : (
                      <div className="text-sm text-gray-700">—</div>
                    )}
                  </div>
                </div>
              )}

              {type === 'TEXT' && (
                <div className="space-y-2 text-sm text-gray-700">
                  <div className="">
                    <div>Texto</div>
                    <div className="mt-2 overflow-hidden rounded bg-gray-900">
                      <div className="p-3" style={{
                        backgroundColor: textBackgroundColor,
                        color: textFontColor,
                        fontFamily: resolveTextAdFontFamily(textFontFamily),
                        fontSize: textFontSize,
                      }}>
                        {campaign.textItems?.length
                          ? <RichMessagePreview items={campaign.textItems} wrap />
                          : <span className="whitespace-pre-wrap [overflow-wrap:anywhere]">{campaign.text || '—'}</span>}
                      </div>
                    </div>
                  </div>
                  {!campaign.textItems?.length && <>
                  <div className="flex items-center justify-between">
                    <span>Tamaño de fuente</span>
                    <span className="font-medium">{campaign.textFontSize ?? '—'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Color de fuente</span>
                    {campaign.textFontColor ? (
                      <span
                        className="inline-flex h-5 w-8 items-center justify-center rounded border border-gray-200"
                        style={{ backgroundColor: campaign.textFontColor }}
                        aria-label={`Color de fuente ${campaign.textFontColor}`}
                        title={campaign.textFontColor}
                      />
                    ) : (
                      <span className="font-medium">—</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Fuente</span>
                    <span className="font-medium">{resolveTextAdFontLabel(textFontFamily)}</span>
                  </div>
                  </>}
                  <div className="flex items-center justify-between">
                    <span>Color de fondo</span>
                    {campaign.textBackgroundColor ? (
                      campaign.textBackgroundColor === 'transparent' ? (
                        <span className="text-xs text-gray-500">Transparente</span>
                      ) : (
                        <span
                          className="inline-flex h-5 w-8 items-center justify-center rounded border border-gray-200"
                          style={{ backgroundColor: campaign.textBackgroundColor }}
                          aria-label={`Color de fondo ${campaign.textBackgroundColor}`}
                          title={campaign.textBackgroundColor}
                        />
                      )
                    ) : (
                      <span className="font-medium">—</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Velocidad del ticker</span>
                    <span className="font-medium">
                    {campaign.textTickerSpeedSeconds ? `${campaign.textTickerSpeedSeconds}s` : '—'}
                  </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Posición</span>
                    <span className="font-medium">
                    {campaign.bannerPosition === 'TOP' ? 'Arriba' : campaign.bannerPosition === 'BOTTOM' ? 'Abajo' : '—'}
                  </span>
                  </div>
                </div>
              )}

              {type === 'NEWS_TICKER' && (
                <div className="space-y-2 text-sm text-gray-700">
                  <div className="flex items-center justify-between">
                    <span>Fuente</span>
                    <span className="font-medium">{newsTickerSourceType}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Máximo de noticias</span>
                    <span className="font-medium">{campaign.newsTickerMaxItems ?? '—'}</span>
                  </div>
                  {campaign.newsTickerRssUrl && (
                    <div>
                      <div>RSS</div>
                      <div className="break-all font-medium">{campaign.newsTickerRssUrl}</div>
                    </div>
                  )}
                  {showManualNews && (
                    <div>
                      <div>Noticias manuales</div>
                      <ul className="mt-1 list-disc space-y-1 pl-5">
                        {manualNewsItems.map((item, index) => (
                          <li key={`${index}-${item}`}>{item}</li>
                        ))}
                      </ul>
                      {manualNewsItems.length === 0 && <div className="font-medium">—</div>}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <TextPreviewModal
        isOpen={isTextPreviewOpen}
        onClose={() => setIsTextPreviewOpen(false)}
        returnFocusRef={previewButtonRef}
        textItems={type === 'TEXT' ? campaign.textItems : undefined}
        text={type === 'NEWS_TICKER' ? newsTickerPreviewText : (campaign.text || '')}
        fontSize={textFontSize}
        fontColor={textFontColor}
        fontFamily={textFontFamily}
        backgroundColor={textBackgroundColor}
        tickerSpeedSeconds={textTickerSpeedSeconds}
        position={textPosition}
      />
      <BannerPreviewModal
        isOpen={isBannerPreviewOpen}
        onClose={() => setIsBannerPreviewOpen(false)}
        returnFocusRef={previewButtonRef}
        imageUrl={campaign.bannerImageUrl}
        position={(campaign.bannerPosition || 'BOTTOM') as BannerOverlayPosition}
        scaleFactor={bannerScaleFactor}
      />
      <VideoPreviewModal
        isOpen={isVideoPreviewOpen}
        onClose={() => setIsVideoPreviewOpen(false)}
        returnFocusRef={previewButtonRef}
        videoUrl={campaign.videoUrl}
      />
      <DisplayNowModal
        isOpen={isDisplayNowOpen}
        onClose={closeDisplayNowModal}
        campaignId={campaign.id}
        campaignName={campaign.name}
      />
    </div>
  );
};

export default ViewCampaign;
